import { AppCustomerContext } from '@/AppCustomerContext';
import { AppFeatures, AppRoles, AppUserTypes, DataRuleKey } from '@/types';
import { useContext } from 'react';

export const useAppCustomers = () => {
  const {
    customerIds,
    setCustomerIds,
    locationIds,
    setLocationIds,
    userPermissions,
    setUserPermissions,
  } = useContext(AppCustomerContext);

  const { subCustomers, roles, userType } = userPermissions;

  const getCustomerDataRule = (customerId: number, ruleKey: DataRuleKey) =>
    subCustomers
      ?.find((o) => o.subCustomerId == customerId)
      ?.rules.find((o) => o.ruleKey == ruleKey);

  const hasFeatureAccess = (feature: AppFeatures, customerId?: number) => {
    if (hasGbaAccess) return true;
    if (!subCustomers) return false;

    const hasAccess = subCustomers.some(
      (o) =>
        (o.subCustomerId == customerId ||
          o.customerId == customerId ||
          customerIds.includes(o.customerId)) &&
        (o.features.includes('All' as AppFeatures) ||
          o.features.includes(feature) ||
          o.features.some(
            (f) => f.includes(`${feature}.`) || feature.includes(`${f}.`),
          )),
    );
    return hasAccess;
  };

  const hasConfiguredFeatureAccess = (feature: AppFeatures) =>
    subCustomers?.some(
      (customer) =>
        customerIds.includes(customer.customerId) &&
        (customer.features.includes('All' as AppFeatures) ||
          customer.features.includes(feature)),
    ) ?? false;

  const hasRoleAccess = (allowedRoles: AppRoles[]) => {
    if (!roles) return false;

    return roles.some((role) => allowedRoles.includes(role));
  };

  const hasUserTypeAccess = (allowedUserTypes: AppUserTypes[]) => {
    if (!userType) return false;

    return allowedUserTypes.includes(userType);
  };

  const hasAdminAccess = hasRoleAccess([AppRoles.Admin, AppRoles.SuperAdmin]);
  const hasSuperAdminAccess = hasRoleAccess([AppRoles.SuperAdmin]);
  const hasLoadAdminAccess = hasRoleAccess([AppRoles.LoadAdmin]);
  const hasLoadLaneOperateAccess = hasRoleAccess([
    AppRoles.LoadAdmin,
    AppRoles.SuperAdmin,
  ]);
  const hasCustomerAccess = hasUserTypeAccess([AppUserTypes.Customers]);
  const hasGbaAccess = hasUserTypeAccess([AppUserTypes.InternalUsers]);
  const hasGbaAdminAccess = hasAdminAccess && hasGbaAccess;
  const hasDamageApproveAccess = hasRoleAccess([AppRoles.DamageApprover]);
  const hasMechanicalUpdateAccess = hasRoleAccess([AppRoles.MechanicalAdmin]);

  return {
    customerIds,
    setCustomerIds,
    locationIds,
    setLocationIds,
    userPermissions,
    setUserPermissions,

    // access boolean
    hasAdminAccess,
    hasSuperAdminAccess,
    hasLoadAdminAccess,
    hasLoadLaneOperateAccess,
    hasCustomerAccess,
    hasGbaAccess,
    hasGbaAdminAccess,
    hasDamageApproveAccess,
    hasMechanicalUpdateAccess,

    hasFeatureAccess,
    hasConfiguredFeatureAccess,
    getCustomerDataRule,
  };
};
